import styled, { css } from 'styled-components'

import { type TicketType } from '~/features/ticket/types/ticket'
import {
  getTicketAmountBackgroundColor,
  getTicketBackgroundImage,
  getTicketTypeBackgroundColor,
} from '~/features/ticket/utils/ticket'
import theme from '~/shared/styles/theme'
import { fCurrency } from '~/shared/utils/format-number'

type Props = {
  data: TicketType
  isMobile: boolean
  onClick: (eventTicketId: number) => void
}

function Ticket({ data, isMobile, onClick }: Props) {
  const { code, name, description: title, event_ticket_list } = data

  return (
    <Wrapper>
      {event_ticket_list.map((eventTicket) => {
        const { amount, discount_amount, id, remain_quantity, description } =
          eventTicket

        const isSoldOut = remain_quantity <= 0
        const discountRate =
          discount_amount > 0 ? Math.ceil((discount_amount / amount) * 100) : 0
        const discountedAmount = amount - discount_amount

        return (
          <TicketContainer
            key={id}
            $backgroundImage={getTicketBackgroundImage(code, isMobile)}
            $isSoldOut={isSoldOut}
            onClick={isSoldOut ? undefined : () => onClick(id)}
          >
            <TicketLeftBox>
              {discountRate > 0 && (
                <TicketSaleTag>{`${Math.round(discountRate)}% 할인`}</TicketSaleTag>
              )}
              <TicketTitle>{title}</TicketTitle>
              <TicketDayTypeBox
                $backgroundColor={getTicketTypeBackgroundColor(code)}
              >
                {description}
              </TicketDayTypeBox>
            </TicketLeftBox>
            <TicketRightBox>
              <TicketDescription>
                {`${description} ${name === 'GGUF' ? '(편의시설 이용권 포함)' : ''}`}
              </TicketDescription>
              <TicketAmount
                $backgroundColor={getTicketAmountBackgroundColor(code)}
              >
                {fCurrency(discountedAmount)}
              </TicketAmount>
            </TicketRightBox>
            {isSoldOut && (
              <Overlay>
                <Line />
                <SoldOutText>Sold Out</SoldOutText>
              </Overlay>
            )}
          </TicketContainer>
        )
      })}
    </Wrapper>
  )
}

export default Ticket

const Wrapper = styled.div`
  cursor: pointer;

  display: flex;
  flex-wrap: wrap;
  gap: 20px;

  box-sizing: border-box;
  width: 100%;

  @media ${theme.device.medium} {
    width: 347px;
  }
`

const TicketContainer = styled.div<{
  $backgroundImage: string
  $isSoldOut: boolean
}>`
  position: relative;

  display: flex;
  flex: 1 1 48%;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  max-width: 530px;
  height: 180px;
  margin: 0 auto;

  background-image: url(${(props) => props.$backgroundImage});
  filter: drop-shadow(6px 7px 8px #c3c3c3);

  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease,
    background-image 0.3s ease;

  ${(props) =>
    props.$isSoldOut &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}

  &:hover {
    ${(props) =>
      !props.$isSoldOut &&
      css`
        transform: translate(-10px, -10px) scale(1.05);
      `}
  }

  @media ${theme.device.medium} {
    flex: 1 1 100%;
  }
`

const TicketLeftBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 200px;
  height: 100%;

  @media ${theme.device.medium} {
    width: 112px;
    height: 100%;
  }
`

const TicketSaleTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 77px;
  height: 21px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 15px;
  font-weight: 700;
  line-height: 21px;
  color: white;

  background-color: #fa6b6b;
`

const TicketTitle = styled.h3`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 30px;
  text-align: center;

  @media ${theme.device.medium} {
    font-size: 16px;
  }
`

const TicketDayTypeBox = styled.div<{ $backgroundColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 126px;
  height: 37px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: white;

  background-color: ${(props) => props.$backgroundColor};
  border-radius: 8px;

  @media ${theme.device.medium} {
    width: 90px;
  }
`

const TicketRightBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 330px;
  height: 100%;

  @media ${theme.device.medium} {
    align-items: center;
    justify-content: center;
    width: 235px;
    padding: 0 20px;
  }
`

const TicketDescription = styled.p`
  font-size: 15px;
  font-weight: 500;
  line-height: 21px;
`

const TicketAmount = styled.span<{ $backgroundColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 127px;
  height: 41px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: #313634;

  background-color: ${(props) => props.$backgroundColor};
  border-radius: 60px;
`

const Overlay = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  width: 100%;
  height: 100%;
`

const Line = styled.div`
  pointer-events: none;

  position: absolute;

  width: 90%;
  height: 3px;

  background-color: #313634;

  @media ${theme.device.medium} {
    width: 80%;
  }
`

const SoldOutText = styled.div`
  pointer-events: none;

  z-index: 3;

  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 60px;
  font-weight: 700;
  line-height: 84px;
  color: #313634;
  text-align: center;
  text-transform: uppercase;
  word-wrap: break-word;

  @media ${theme.device.medium} {
    font-size: 36px;
    line-height: 50px;
  }
`
